import React, { useEffect, useState } from 'react';
import { apiGet } from '../../../lib/api';
import { Activity, ClipboardList, Filter, RefreshCw, CheckCircle2, XCircle } from 'lucide-react';
import { useUrlTab } from '../../../lib/hooks/useUrlState';
import { formatDateTime } from '../../../lib/format';

const Tab = ({ active, onClick, children, testId }) => (
  <button data-testid={testId} onClick={onClick}
    className={`px-4 py-2 text-sm font-bold border-b-2 -mb-px transition-colors ${
      active ? 'border-[#3b4872] text-[#3b4872]' : 'border-transparent text-slate-500 hover:text-slate-700'
    }`}>
    {children}
  </button>
);

export default function VendorPortalDiagnostics() {
  const [tab, setTab] = useUrlTab('tab', 'logins');
  const [days, setDays] = useState(7);
  const [emailFilter, setEmailFilter] = useState('');
  const [successFilter, setSuccessFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      if (tab === 'logins') {
        const params = { days };
        if (emailFilter.trim()) params.email = emailFilter.trim();
        if (successFilter !== '') params.success = successFilter;
        const data = await apiGet('/vendor-portal-admin/diagnostics/login-attempts', params);
        setRows(data.items || []);
      } else {
        const params = { days };
        if (actionFilter.trim()) params.action = actionFilter.trim();
        const data = await apiGet('/vendor-portal-admin/diagnostics/admin-actions', params);
        setRows(data.items || []);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [tab, days]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="vendor-portal-diagnostics" className="space-y-4">
      <div>
        <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">Vendor Portal Admin</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight flex items-center gap-2">
          <Activity size={26} /> Diagnostics & Logs
        </h1>
        <p className="text-sm text-slate-600 mt-1">Login attempts (success + failure) and audit trail of admin actions.</p>
      </div>

      <div className="border-b border-slate-200">
        <Tab active={tab === 'logins'} onClick={() => setTab('logins')} testId="tab-logins">
          Login Attempts
        </Tab>
        <Tab active={tab === 'actions'} onClick={() => setTab('actions')} testId="tab-actions">
          Admin Action Log
        </Tab>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Filter size={14} className="text-slate-400" />
        <select data-testid="days-filter" value={days} onChange={e => setDays(Number(e.target.value))}
          className="border border-slate-300 rounded-sm text-sm py-2 px-2">
          <option value={1}>Last 24 hours</option>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
        {tab === 'logins' && (
          <>
            <input data-testid="email-filter" value={emailFilter} onChange={e => setEmailFilter(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && load()}
              placeholder="Filter by email"
              className="border border-slate-300 rounded-sm text-sm py-2 px-2" />
            <select data-testid="success-filter" value={successFilter} onChange={e => setSuccessFilter(e.target.value)}
              className="border border-slate-300 rounded-sm text-sm py-2 px-2">
              <option value="">All attempts</option>
              <option value="true">Successful only</option>
              <option value="false">Failed only</option>
            </select>
          </>
        )}
        {tab === 'actions' && (
          <input data-testid="action-filter" value={actionFilter} onChange={e => setActionFilter(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && load()}
            placeholder="Filter by action (e.g. suspend_vendor)"
            className="border border-slate-300 rounded-sm text-sm py-2 px-2" />
        )}
        <button data-testid="apply-filters" onClick={load}
          className="px-3 py-2 border border-slate-300 rounded-sm text-sm hover:bg-slate-50 flex items-center gap-1">
          <RefreshCw size={14} /> Refresh
        </button>
        <div className="ml-auto text-xs text-slate-500">{rows.length} record(s)</div>
      </div>

      <div className="rounded-sm border border-slate-200 bg-white overflow-x-auto">
        {tab === 'logins' ? (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left p-3">When</th>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">Result</th>
                <th className="text-left p-3">Reason</th>
                <th className="text-left p-3">IP</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={5} className="p-6 text-center text-slate-500">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-500">No attempts in this window.</td></tr>}
              {!loading && rows.map((r, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="p-3 text-xs">{formatDateTime(r.attempted_at)}</td>
                  <td className="p-3 font-medium">{r.email}</td>
                  <td className="p-3">
                    {r.success ? (
                      <span className="text-emerald-600 inline-flex items-center gap-1 font-medium"><CheckCircle2 size={14} /> OK</span>
                    ) : (
                      <span className="text-red-600 inline-flex items-center gap-1 font-medium"><XCircle size={14} /> Failed</span>
                    )}
                  </td>
                  <td className="p-3 text-xs text-slate-600 font-mono">{r.reason || '—'}</td>
                  <td className="p-3 text-xs text-slate-500 font-mono">{r.ip || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left p-3">When</th>
                <th className="text-left p-3">Admin</th>
                <th className="text-left p-3">Action</th>
                <th className="text-left p-3">Target</th>
                <th className="text-left p-3">Detail</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={5} className="p-6 text-center text-slate-500">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-500">No admin actions in this window.</td></tr>}
              {!loading && rows.map((r, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="p-3 text-xs">{formatDateTime(r.at)}</td>
                  <td className="p-3 font-medium">{r.by_name || r.by_email || '—'}</td>
                  <td className="p-3 text-xs font-mono bg-slate-50 inline-block px-2 rounded-sm">{r.action}</td>
                  <td className="p-3 text-xs text-slate-500 font-mono">{r.target_id}</td>
                  <td className="p-3 text-xs text-slate-600">{r.detail || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="text-xs text-slate-400 flex items-center gap-1">
        <ClipboardList size={12} /> Records are append-only — every admin action and every login attempt is permanently logged.
      </div>
    </div>
  );
}
